import { useState, useRef, useLayoutEffect, memo } from 'react';
import type { FilaJukebox } from '@/shared/models/jukebox.model';
import { getClientePrimeiroNome } from '../../domain/usecases/clienteDisplay';
import { MobileSheet } from '@/shared/components/MobileSheet';

interface Props {
  filaPendente:  FilaJukebox[];
  meuSessionId:  string;
  clienteNome?:  string | null;
  queueIcon:     string;
  /** ETA do primeiro pedido pendente da sessão (calculado em JukeboxCliente). */
  etaLabel?:     string | null;
}

const ITEM_W = 80;   // w-20
const ITEM_GAP = 12; // gap-3
const VER_FILA_W = 64; // w-16
const ITENS_TIRA_INICIAL = 3;

/** Quantos itens cabem na tira; o excedente vira o botão "+N ver fila". */
function capacidadeTira(larguraPx: number, total: number): number {
  const cabemTodos = Math.floor((larguraPx + ITEM_GAP) / (ITEM_W + ITEM_GAP));
  if (total <= cabemTodos) return total;
  return Math.max(1, Math.floor((larguraPx - VER_FILA_W) / (ITEM_W + ITEM_GAP)));
}

function JukeboxFilaSectionImpl({ filaPendente, meuSessionId, clienteNome, queueIcon, etaLabel }: Props) {
  const [expandida, setExpandida] = useState(false);
  const tiraRef = useRef<HTMLDivElement>(null);
  const [larguraTira, setLarguraTira] = useState<number | null>(null);
  const filaVazia = filaPendente.length === 0;
  useLayoutEffect(() => {
    const el = tiraRef.current;
    if (!el) return;
    const medir = () => setLarguraTira(el.clientWidth - 8);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filaVazia]);
  const primeiroNome = getClientePrimeiroNome(clienteNome);
  if (filaPendente.length === 0) return null;

  const meuPrimeiroPendenteId = filaPendente.find(
    (f) => f.session_id === meuSessionId && f.status === 'pendente',
  )?.id;

  const limiteTira = larguraTira === null
    ? ITENS_TIRA_INICIAL
    : capacidadeTira(larguraTira, filaPendente.length);
  const visiveis   = filaPendente.slice(0, limiteTira);
  const restantes  = filaPendente.length - visiveis.length;
  let posicaoPendente = 0;

  return (
    <>
      <div aria-label="Fila de músicas" role="region">
        <div className="mb-2 flex items-center gap-2">
          <svg className={`h-3.5 w-3.5 ${queueIcon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          <span className="flex-1 text-xs font-medium text-white/55">
            Fila
          </span>
          <span className="font-mono text-xs tabular-nums text-white/50">{filaPendente.length}</span>
        </div>

        <div ref={tiraRef} className="flex gap-3 overflow-hidden pt-2 pb-1 px-1 -mx-1">
          {visiveis.map((f) => {
            const meuPedido   = f.session_id === meuSessionId;
            const tocandoAgora = f.status === 'tocando';
            if (!tocandoAgora) posicaoPendente += 1;
            return (
              <div key={f.id} className="shrink-0 w-20 text-center">
                <div className="relative w-14 h-14 mx-auto">
                  {f.thumbnail ? (
                    <img
                      src={f.thumbnail}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className={`w-14 h-14 rounded-xl object-cover ${
                        tocandoAgora
                          ? 'ring-2 ring-orange-400/70'
                          : meuPedido
                            ? 'ring-2 ring-emerald-400/60'
                            : 'opacity-80'
                      }`}
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-white/[0.06]" />
                  )}
                  {tocandoAgora ? (
                    <span
                      className="absolute -left-1.5 -top-1.5 z-10 flex h-6 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white shadow-sm ring-2 ring-jukebox-bg animate-pulse-slow"
                      aria-label="No ar"
                    >
                      No ar
                    </span>
                  ) : (
                    <span className="absolute -top-1.5 -left-1.5 z-10 min-w-6 h-6 px-0.5 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-jukebox-bg shadow-sm">
                      {posicaoPendente}
                    </span>
                  )}
                </div>
                <p className={`text-[11px] mt-1 line-clamp-2 leading-tight ${
                  tocandoAgora
                    ? 'text-orange-400 font-semibold'
                    : meuPedido
                      ? 'text-emerald-400 font-semibold'
                      : 'text-white/60'
                }`}>
                  {tocandoAgora
                    ? 'No ar'
                    : meuPedido
                      ? (f.id === meuPrimeiroPendenteId && etaLabel ? etaLabel : (primeiroNome ?? 'Você'))
                      : f.titulo}
                </p>
              </div>
            );
          })}

          {restantes > 0 && (
            <button
              onClick={() => setExpandida(true)}
              aria-label={`Ver fila completa, mais ${restantes} música${restantes > 1 ? 's' : ''}`}
              className="shrink-0 w-16 h-14 rounded-xl bg-white/[0.06] flex flex-col items-center justify-center text-white/60 min-w-11 min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
            >
              <span className="text-xs font-bold">+{restantes}</span>
              <span className="text-[11px] text-white/55">ver fila</span>
            </button>
          )}
        </div>
      </div>

      <MobileSheet
        aberto={expandida}
        titulo="Fila completa"
        onClose={() => setExpandida(false)}
      >
        <div className="flex shrink-0 items-center justify-between px-5 pb-3">
          <h3 className="text-white font-bold text-base">Fila completa</h3>
          <button
            onClick={() => setExpandida(false)}
            aria-label="Fechar"
            className="w-11 h-11 rounded-full bg-white/[0.06] flex items-center justify-center text-white/50 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-white/[0.04] pb-6">
          {(() => {
            let posPendente = 0;
            return filaPendente.map((f) => {
              const meuPedido    = f.session_id === meuSessionId;
              const tocandoAgora = f.status === 'tocando';
              if (!tocandoAgora) posPendente += 1;
              return (
                <div
                  key={f.id}
                  className={`flex items-center gap-3 px-5 py-3 ${
                    tocandoAgora
                      ? 'bg-orange-500/[0.06]'
                      : meuPedido
                        ? 'bg-emerald-500/[0.05]'
                        : ''
                  }`}
                >
                  {tocandoAgora ? (
                    <span className="w-5 shrink-0 flex justify-center" aria-hidden>
                      <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse-slow" />
                    </span>
                  ) : (
                    <span className="w-5 shrink-0 text-center text-xs font-mono text-white/55">{posPendente}</span>
                  )}
                  {f.thumbnail && (
                    <img
                      src={f.thumbnail}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className={`w-10 h-10 rounded-lg object-cover shrink-0 ${tocandoAgora ? 'ring-1 ring-orange-400/50' : 'opacity-80'}`}
                    />
                  )}
                  <p className={`text-sm truncate flex-1 ${
                    tocandoAgora
                      ? 'text-orange-300 font-medium'
                      : meuPedido
                        ? 'text-white font-medium'
                        : 'text-white/65'
                  }`}>
                    {f.titulo}
                  </p>
                  {tocandoAgora ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-orange-500/15 px-2 py-0.5 text-[11px] font-semibold text-orange-400 ring-1 ring-orange-500/25">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-400" />
                      No ar
                    </span>
                  ) : meuPedido ? (
                    <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-semibold border border-emerald-500/20">
                      {primeiroNome ?? 'Você'}
                      {f.id === meuPrimeiroPendenteId && etaLabel ? ` · ${etaLabel}` : ''}
                    </span>
                  ) : null}
                </div>
              );
            });
          })()}
        </div>
      </MobileSheet>
    </>
  );
}

export const JukeboxFilaSection = memo(JukeboxFilaSectionImpl);
